import { useState, useEffect, useCallback } from 'react';
import { Button, ConfirmDialog, Input } from '@agendor/ui';
import { KnowledgeApi } from '@agendor/shared';
import {
    FormGroup,
    InputGroup,
    TextArea,
    HelperText,
    Checkbox,
    CheckboxLabel,
    LabelRow,
    WarningBanner,
    Divider,
} from '../components/Input';
import { Modal } from '../components/Modal';
import {
    MasterDetail,
    MasterList,
    MasterItem,
    MasterItemMeta,
    DetailPanel,
    EmptyState,
    DetailPlaceholder,
} from '../components/MasterDetail';
import BusinessService, { BusinessDetails, CreditsDepletedError, KnowledgeEntry } from '../business-service';
import { useSingleModalRoute } from '../navigation';
import { useToast } from '../toast-context';
import { ConfigCard, ConfigInfo, ConfigTitle, ConfigSummary, SummaryItem } from './AssistantSection';

type OpenScopeCardProps = {
    business: BusinessDetails;
    service: BusinessService | null;
    onReloadBusiness: () => Promise<void> | void;
    isActive: boolean;
    modalSegments: string[];
    onNavigate: (segments: string[], replace?: boolean) => void;
};

// The modal edits a copy of the list. Nothing reaches the server until Salvar,
// which is what lets Cancelar mean something.
type EntryDraft = { id: number | null; title: string; summary: string; content: string };

const emptyDraft: EntryDraft = { id: null, title: '', summary: '', content: '' };

export default function OpenScopeCard({
    business,
    service,
    onReloadBusiness,
    isActive,
    modalSegments,
    onNavigate,
}: OpenScopeCardProps) {
    const [entries, setEntries] = useState<KnowledgeEntry[] | null>(null);
    const [drafts, setDrafts] = useState<EntryDraft[]>([]);
    const [removedIds, setRemovedIds] = useState<number[]>([]);
    const [selected, setSelected] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);
    const [indexToRemove, setIndexToRemove] = useState<number | null>(null);
    const [drafting, setDrafting] = useState(false);
    // The content the current title and summary describe, so regenerating the
    // same text — which would cost credits and return the same pair — is off.
    const [draftedFrom, setDraftedFrom] = useState<string | null>(null);

    // The knowledge routes only exist while the saved setting is on.
    const knowledgeActive = business.openScope?.enabled === true;
    const atEntryLimit = drafts.length >= KnowledgeApi.MAX_ENTRIES;

    const { showToast } = useToast();

    const { isOpen, open, close } = useSingleModalRoute({
        isActive,
        ready: true,
        segments: modalSegments,
        onNavigate,
        modalKey: 'open-scope',
    });

    // Nested one level in, so the knowledge modal is its own address and closing
    // it lands back on the setting instead of on the section.
    const {
        isOpen: isKnowledgeOpen,
        open: openKnowledge,
        close: closeKnowledge,
    } = useSingleModalRoute({
        isActive: isOpen,
        ready: entries !== null,
        segments: modalSegments.slice(1),
        onNavigate,
        modalKey: 'knowledge',
        baseSegments: ['open-scope'],
    });

    // Entries carry their full content, so they are fetched when the list is
    // opened rather than on every page load; the card shows a count that rides
    // along with the business itself.
    const loadEntries = useCallback(async () => {
        if (!service || !knowledgeActive || !isOpen) {
            setEntries(null);
            return;
        }
        try {
            setEntries(await service.listKnowledge());
        } catch (error) {
            console.error('Failed to load knowledge', error);
        }
    }, [service, knowledgeActive, isOpen]);

    useEffect(() => {
        loadEntries();
    }, [loadEntries]);

    useEffect(() => {
        if (!isKnowledgeOpen) return;
        setDrafts((entries ?? []).map((entry) => ({ ...entry })));
        setRemovedIds([]);
        setSelected(null);
        setIndexToRemove(null);
        setDraftedFrom(null);
    }, [isKnowledgeOpen, entries]);

    if (!business.openScope) return null;

    // The setting persists on toggle instead of on a save button: knowledge
    // lives behind routes that only exist while it is on, so the section can
    // only appear once the server knows about it.
    const handleToggleEnabled = async (nextEnabled: boolean) => {
        if (!service) return;

        setSaving(true);
        try {
            await service.setOpenScopeEnabled({ enabled: nextEnabled });
            await onReloadBusiness();
        } catch (error) {
            console.error('Failed to save open scope', error);
            showToast('Não foi possível salvar a configuração.', 'error', 'modal');
        } finally {
            setSaving(false);
        }
    };

    const selectedDraft = selected === null ? null : (drafts[selected] ?? null);

    const startAdd = () => {
        setDrafts((previous) => [...previous, { ...emptyDraft }]);
        setSelected(drafts.length);
        setDraftedFrom(null);
    };

    const selectDraft = (index: number) => {
        setSelected(index);
        setDraftedFrom(drafts[index].content);
    };

    const patchSelected = (patch: Partial<EntryDraft>) => {
        if (selected === null) return;
        setDrafts((previous) => previous.map((draft, index) => (index === selected ? { ...draft, ...patch } : draft)));
    };

    const removeDraft = () => {
        if (indexToRemove === null) return;
        const draft = drafts[indexToRemove];
        if (draft.id !== null) setRemovedIds((previous) => [...previous, draft.id!]);
        setDrafts((previous) => previous.filter((_, index) => index !== indexToRemove));
        setSelected(null);
        setIndexToRemove(null);
    };

    const canRedraft =
        selectedDraft !== null && selectedDraft.content.trim() !== '' && selectedDraft.content.trim() !== draftedFrom;

    // Where the selected entry would land if the list were saved now. The tier
    // depends on the whole set, not on one entry, so it is read off the drafts.
    const trimmedDrafts = drafts.map((draft) => ({ content: draft.content.trim() }));
    const summaryIsUsed =
        selected !== null &&
        trimmedDrafts[selected].content !== '' &&
        !KnowledgeApi.inlineIndices(trimmedDrafts).has(selected);

    const handleRedraft = async () => {
        if (!service || selectedDraft === null) return;
        const content = selectedDraft.content.trim();
        setDrafting(true);
        try {
            const { title, summary } = await service.draftKnowledge({ content, knowledgeId: selectedDraft.id });
            patchSelected({ title, summary });
            setDraftedFrom(content);
        } catch (error) {
            console.error('Failed to draft knowledge', error);
            showToast(
                error instanceof CreditsDepletedError
                    ? 'Seus créditos de IA acabaram. Escreva o título e o resumo, ou compre mais créditos.'
                    : 'Não foi possível gerar o título e o resumo.',
                'error',
                'modal',
            );
        } finally {
            setDrafting(false);
        }
    };

    const handleSaveKnowledge = async () => {
        if (!service) return;

        for (const draft of drafts) {
            if (!draft.content.trim()) {
                showToast('Todos os registros devem ter um conteúdo.', 'error', 'modal');
                return;
            }
            if (!draft.title.trim()) {
                showToast('Todos os registros devem ter um título.', 'error', 'modal');
                return;
            }
        }

        setSaving(true);
        try {
            // Removals first: freeing a slot is what lets an addition through the
            // entry limit in the same save. Each one is dropped as it lands, so a
            // failure halfway does not replay it when the owner saves again.
            for (const knowledgeId of [...removedIds]) {
                await service.deleteKnowledge({ knowledgeId });
                setRemovedIds((previous) => previous.filter((id) => id !== knowledgeId));
            }
            const savedById = new Map((entries ?? []).map((entry) => [entry.id, entry]));
            for (const draft of drafts) {
                const saved = draft.id === null ? undefined : savedById.get(draft.id);
                const payload = {
                    title: draft.title.trim(),
                    summary: draft.summary.trim() || null,
                    content: draft.content.trim(),
                };
                if (draft.id === null) {
                    await service.createKnowledge(payload);
                    continue;
                }
                // Untouched entries have nothing to write.
                const unchanged =
                    saved !== undefined &&
                    saved.title === payload.title &&
                    saved.content === payload.content &&
                    (payload.summary === null || saved.summary === payload.summary);
                if (!unchanged) await service.updateKnowledge({ knowledgeId: draft.id, ...payload });
            }
            await loadEntries();
            await onReloadBusiness();
            showToast('Conhecimento salvo com sucesso!', 'success', 'app');
            closeKnowledge();
        } catch (error) {
            console.error('Failed to save knowledge', error);
            showToast('Não foi possível salvar o conhecimento.', 'error', 'modal');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <ConfigCard data-testid="open-scope-card" onClick={() => open()} style={{ marginTop: '12px' }}>
                <ConfigInfo>
                    <ConfigTitle>Atendimento ampliado</ConfigTitle>
                    <ConfigSummary>
                        <SummaryItem>
                            {business.openScope.enabled ? 'Atende além de agendamentos' : 'Somente agendamentos'}
                        </SummaryItem>
                        {business.openScope.knowledgeCount > 0 && (
                            <SummaryItem>
                                {business.openScope.knowledgeCount === 1
                                    ? '1 registro de conhecimento'
                                    : `${business.openScope.knowledgeCount} registros de conhecimento`}
                            </SummaryItem>
                        )}
                    </ConfigSummary>
                </ConfigInfo>
            </ConfigCard>

            <Modal isOpen={isOpen} onClose={close} title="Atendimento ampliado" data-testid="open-scope-modal">
                <FormGroup>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <CheckboxLabel>
                            <Checkbox
                                data-testid="open-scope-enabled-checkbox"
                                checked={business.openScope.enabled}
                                onChange={(e) => handleToggleEnabled(e.target.checked)}
                                disabled={saving}
                            />
                            Ativar atendimento ampliado
                        </CheckboxLabel>
                        <HelperText>
                            O assistente responde sobre qualquer produto ou serviço do negócio, além de cuidar dos
                            agendamentos.
                        </HelperText>
                    </div>

                    <WarningBanner $severity="info">
                        Com mais assuntos nas conversas, o consumo de créditos de IA tende a aumentar.
                    </WarningBanner>

                    {knowledgeActive ? (
                        <InputGroup style={{ width: '100%' }}>
                            <LabelRow>
                                Conhecimento
                                <HelperText data-testid="knowledge-count">
                                    {business.openScope.knowledgeCount} de {KnowledgeApi.MAX_ENTRIES} registros
                                </HelperText>
                            </LabelRow>
                            <div style={{ display: 'flex', marginTop: '8px' }}>
                                <Button data-testid="knowledge-manage-button" onClick={() => openKnowledge()}>
                                    Gerenciar conhecimento
                                </Button>
                            </div>
                        </InputGroup>
                    ) : (
                        <HelperText>
                            Ative para adicionar conhecimento personalizado às respostas do assistente.
                        </HelperText>
                    )}
                </FormGroup>
            </Modal>

            <Modal
                isOpen={isKnowledgeOpen}
                onClose={closeKnowledge}
                title="Conhecimento"
                size="wide"
                data-testid="knowledge-modal"
                onBack={selected === null ? undefined : () => setSelected(null)}
                footer={
                    <>
                        <Button data-testid="modal-cancel" onClick={closeKnowledge} disabled={saving || drafting}>
                            Cancelar
                        </Button>
                        <Button
                            primary
                            data-testid="knowledge-save-button"
                            onClick={handleSaveKnowledge}
                            disabled={saving || drafting}
                        >
                            {saving ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </>
                }
            >
                <FormGroup>
                    <HelperText>
                        O que o assistente deve saber para responder clientes: detalhes de cursos e produtos, políticas,
                        formas de pagamento.
                    </HelperText>

                    {drafts.length === 0 ? (
                        <EmptyState>
                            <HelperText style={{ margin: 0 }}>Nenhum registro cadastrado.</HelperText>
                            <Button primary data-testid="knowledge-add-button" onClick={startAdd} disabled={saving}>
                                Adicionar registro
                            </Button>
                        </EmptyState>
                    ) : (
                        <MasterDetail $hasSelection={selected !== null}>
                            <MasterList>
                                {drafts.map((draft, index) => (
                                    <MasterItem
                                        key={draft.id ?? `new-${index}`}
                                        data-testid="knowledge-entry"
                                        $active={selected === index}
                                        onClick={() => selectDraft(index)}
                                    >
                                        <span>{draft.title || '(sem título)'}</span>
                                        <MasterItemMeta>{draft.summary || draft.content}</MasterItemMeta>
                                    </MasterItem>
                                ))}
                                <Button
                                    data-testid="knowledge-add-button"
                                    onClick={startAdd}
                                    disabled={saving || atEntryLimit}
                                    style={{ marginTop: 6 }}
                                >
                                    Adicionar registro
                                </Button>
                                {atEntryLimit && <HelperText>Remova um registro para adicionar outro.</HelperText>}
                            </MasterList>

                            <DetailPanel>
                                {selectedDraft === null ? (
                                    <DetailPlaceholder>Selecione um registro ou crie um novo.</DetailPlaceholder>
                                ) : (
                                    <>
                                        <InputGroup style={{ width: '100%' }}>
                                            <LabelRow>Conteúdo</LabelRow>
                                            <TextArea
                                                data-testid="knowledge-content-input"
                                                value={selectedDraft.content}
                                                onChange={(e) => patchSelected({ content: e.target.value })}
                                                maxLength={KnowledgeApi.MAX_CONTENT_CHARS}
                                                rows={8}
                                                placeholder="Ex: O curso de depilação custa R$ 697, dura 3 semanas e inclui certificado. Turmas começam todo início de mês."
                                            />
                                        </InputGroup>

                                        <Divider />

                                        <InputGroup style={{ width: '100%' }}>
                                            <LabelRow>Título</LabelRow>
                                            <Input
                                                data-testid="knowledge-title-input"
                                                value={selectedDraft.title}
                                                onChange={(e) => patchSelected({ title: e.target.value })}
                                                maxLength={KnowledgeApi.MAX_TITLE_CHARS}
                                            />
                                        </InputGroup>

                                        <InputGroup style={{ width: '100%' }}>
                                            <LabelRow>Resumo (opcional)</LabelRow>
                                            <TextArea
                                                data-testid="knowledge-summary-input"
                                                value={selectedDraft.summary}
                                                onChange={(e) => patchSelected({ summary: e.target.value })}
                                                maxLength={KnowledgeApi.MAX_SUMMARY_CHARS}
                                                rows={2}
                                                placeholder="Em branco, usamos o começo do conteúdo."
                                            />
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                <CheckboxLabel style={{ fontWeight: 400 }}>
                                                    <Checkbox
                                                        data-testid="knowledge-summary-in-use"
                                                        checked={summaryIsUsed}
                                                        disabled
                                                    />
                                                    Em uso pelo assistente
                                                </CheckboxLabel>
                                                <HelperText>
                                                    O assistente lê o resumo quando o conteúdo é longo demais para
                                                    entrar inteiro na conversa, e busca o registro se ele responder à
                                                    pergunta do cliente.
                                                </HelperText>
                                            </div>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-start',
                                                    gap: '12px',
                                                    marginTop: '12px',
                                                }}
                                            >
                                                <Button
                                                    data-testid="knowledge-draft-button"
                                                    onClick={handleRedraft}
                                                    disabled={!canRedraft || drafting || saving}
                                                >
                                                    {drafting ? 'Gerando...' : 'Gerar título e resumo com IA'}
                                                </Button>
                                                <HelperText>Gerar com IA usa créditos.</HelperText>
                                            </div>
                                        </InputGroup>

                                        <Divider />

                                        <Button
                                            danger
                                            data-testid="knowledge-delete-button"
                                            onClick={() => setIndexToRemove(selected)}
                                            disabled={saving}
                                            style={{ alignSelf: 'flex-start' }}
                                        >
                                            Remover registro
                                        </Button>
                                    </>
                                )}
                            </DetailPanel>
                        </MasterDetail>
                    )}
                </FormGroup>
            </Modal>

            {indexToRemove !== null && (
                <ConfirmDialog
                    isOpen={true}
                    title="Remover registro"
                    message={
                        <>
                            Tem certeza que deseja remover o registro{' '}
                            <strong>{drafts[indexToRemove].title || '(sem título)'}</strong>? O assistente deixará de
                            usar essa informação quando você salvar.
                        </>
                    }
                    confirmText="Remover"
                    onConfirm={removeDraft}
                    onCancel={() => setIndexToRemove(null)}
                    isLoading={saving}
                />
            )}
        </>
    );
}
